import React, { useEffect, useRef, useState } from "react";

import { CircularProgress, Tooltip } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  RowActionsExtra,
  Constants as GridConstants,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEdit } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { Sms as SmsIcon } from "iconsax-react";

import BadgeStatus from "Components/Generic/BadgeStatus";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "Constants";
import { useAppState } from "Context/StateContext";
import { Agent, Agent as IAgent } from "Models/agent";
import { Contact } from "Models/contact";
import { Location } from "Models/location";
import { Filters, Searches } from "Models/shipeezi.table";
import AgentService from "Services/agent.service";
import { AgentStatus, Status } from "Util/enums";
import useStyles from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";
import { useAgentCreateAndEditActions } from "../../hooks/useAgentCreateAndEditActions";
import { Share as ShareIcon } from "iconsax-react";
import { Box } from "@mui/material";
import { AppConfirmationModal, AppConfirmationModalRef } from "Components/AppConfirmationModal";

export const AgentListPage = () => {
  const [agents, setAgents] = useState<IAgent[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();

  const [total, setTotal] = useState<number>(0);

  const { currentUser, userPermissions } = useAppState();
  const modalRef = useRef<AppConfirmationModalRef>(null);

  const { useInviteAgent } = useAgentCreateAndEditActions();

  const history = useHistory();

  const classes = useStyles();

  const { t: translate } = useTranslation();
  const [agentPermission, setAgentPermission] = useState<{
    view: boolean;
    edit: boolean;
    create: boolean;
    changeStatus: boolean;
  }>({
    view: false,
    edit: false,
    create: false,
    changeStatus: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setAgentPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_VIEW),
        edit:
          checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_ADD_NEW),
        changeStatus:
          checkResourceCan(userPermissions, ResourceName.AGENTS, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_AGENTS_CHANGE_STATUS),
      });
    }
  }, [userPermissions]);

  const { isFetching: loading, refetch: refetchAgentsList } = useQuery(
    ["agents.list", page, pageSize, filters, searches],
    () =>
      AgentService.getAll({
        page,
        pageSize,
        createdCompanyId: currentUser?.companyId,
        include: JSON.stringify([
          { association: "contacts" },
          { association: "locations" },
          { association: "associatedCompany" },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => setAgents(response?.data),
    }
  );

  useQuery(
    ["agents.count", searches, filters],
    () =>
      AgentService.count({
        createdCompanyId: currentUser?.companyId,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      onSuccess: (response) => setTotal(response?.data),
    }
  );

  const { refetch: refetchActiveAgentsCountList } = useQuery(
    ["agents.active.count"],
    () =>
      AgentService.count({
        createdCompanyId: currentUser?.companyId,
        status: Status.ACTIVE,
      }),
    { enabled: !!currentUser?.id }
  );
  const { mutate: inviteAgent, isLoading: isLoadingInvite } = useInviteAgent({
    onSuccess: () => refetchAgentsList(),
  });

  const { mutate: updateAgent } = useMutation(
    ["agents.status.update"],
    ({ id, agent }: { id: number; agent: IAgent }) => AgentService.update(id, agent),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        refetchAgentsList();
        refetchActiveAgentsCountList();
      },
    }
  );

  const locationFormat = (locations: Location[]) => {
    if (!locations || !locations.length) return "";

    const location = locations.find((location) => ["Primary", "main"].includes(location["type"]!));

    if (location && location.country && location.suburb) {
      return `${location.suburb} - ${location.country}`;
    }

    return "";
  };

  const mainContactName = (value: Contact[]) => {
    if (!value?.length) return "";

    const mainContact = value.find((contact) => contact.isPrimary === true);

    if (mainContact?.lastName && mainContact?.firstName) {
      return `${mainContact.firstName} ${mainContact.lastName}`;
    }

    return "";
  };

  const renderNameAndNetwork = (agent: IAgent) => {
    const name = agent?.entity || agent?.name || "";
    if (!agent?.isShipeeziNetwork) return name;

    return (
      <Box display="flex" alignItems="center" gap="12px">
        {name}

        <Tooltip
          data-cy="action-details"
          title={translate("agentIsConnectedToTheShipeezisNetwork")}
          placement="top"
        >
          <div>
            <ShareIcon size={12} />
          </div>
        </Tooltip>
      </Box>
    );
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const editAgent = (agentId: number | string) => {
    if (agentId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.NETWORK_AGENT_MNT}/${agentId}`,
        state: {
          agentId: Number(agentId),
          tab: "agents",
        },
      });
    }
  };

  const viewAgent = (agentId: string | number) => {
    if (agentId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.NETWORK_AGENT_MNT}/${agentId}`,
        state: {
          viewOnly: true,
          agentId: Number(agentId),
          tab: "agents",
        },
      });
    }
  };

  const activeOrInactiveAgent = (id: string | number, agent: IAgent) => {
    if (agent) {
      const agentUpdate: IAgent = {
        ...agent,
        status: agent.status === AgentStatus.DISABLED ? AgentStatus.ACTIVE : AgentStatus.DISABLED,
      };

      if (agentUpdate && agentUpdate?.id) updateAgent({ id: agentUpdate?.id, agent: agentUpdate });
    }
  };

  const inviteAgentHandler = async (agent: IAgent) => {
    const result: boolean =
      (await modalRef.current?.open({
        title: translate("inviteToNetwork"),
        message: translate("wouldYouLikeToSendAnInviteToAgentName", {
          agentName: agents.find((a) => a.id === agent?.id)?.entity || "",
        }),
      })) ?? false;

    if (!result) return;

    inviteAgent({ agent });
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      key: "edit",
      buttonAndAction: (id: string | number, agent: IAgent) => {
        if (!isShowAction(currentUser, agentPermission.edit)) return null;

        if (
          [
            AgentStatus.ACTIVE,
            AgentStatus.INVITATION_SENT,
            AgentStatus.INVITATION_EXPIRED,
            AgentStatus.REJECTED,
          ].includes(agent?.status as AgentStatus)
        ) {
          return (
            <Tooltip title={translate("edit")} placement="top">
              <div>
                <FiEdit
                  className={classes.iconExtraAction}
                  size={GridConstants.defaultRowActionSize}
                  color={GridConstants.defaultColorIcon}
                  onClick={() => editAgent(id)}
                  data-cy="action-edit"
                />
              </div>
            </Tooltip>
          );
        }
      },
    },
    {
      title: translate("inviteAgent"),
      key: "inviteAgent",
      buttonAndAction: (_id: number, agent: IAgent) => {
        if (!isShowAction(currentUser, agentPermission.edit)) return null;
        if (!agent) return null;
        if (
          ![
            AgentStatus.ACTIVE,
            AgentStatus.INVITATION_EXPIRED,
            AgentStatus.INVITATION_SENT,
            AgentStatus.REJECTED,
          ].includes(agent.status as AgentStatus)
        )
          return null;
        if (agent?.networkToken || agent?.status == AgentStatus.INVITATION_SENT) return null;

        return (
          <Tooltip title={translate("invite")} placement="top">
            <div>
              {isLoadingInvite ? (
                <CircularProgress size={GridConstants.defaultRowActionSize} />
              ) : (
                <SmsIcon
                  className={classes.iconExtraAction}
                  size={GridConstants.defaultRowActionSize}
                  color={GridConstants.defaultColorIcon}
                  data-cy="action-invite-agent"
                  onClick={() => inviteAgentHandler(agent)}
                />
              )}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <ShipeeziNewGrid
        data={agents}
        columns={[
          {
            name: "entity",
            title: translate("name"),
            formatComponent: (_value: string, raw: Agent) => renderNameAndNetwork(raw),
          },
          {
            name: "contacts",
            title: translate("primaryContact"),
            formatComponent: mainContactName,
            hideSearch: true,
          },
          {
            name: "phone",
            title: translate("phone"),
          },
          { name: "email", title: translate("email") },
          {
            name: "locations",
            title: translate("location"),
            formatText: locationFormat,
            hideSearch: true,
          },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (value?: string) => <BadgeStatus value={value} />,
            hideSearch: true,
          },
        ]}
        namePage={translate("agents")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        totalItems={total}
        loadingData={loading}
        emptyData={translate("noInformationToBeShown")}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        rowActions={{
          orderBy: ["edit", "handleDetails", "handleChange", "inviteAgent"],
          handleDetails: isShowAction(currentUser, agentPermission.view)
            ? (rowId) => viewAgent(rowId)
            : undefined,
          handleChange: isShowAction(currentUser, agentPermission.changeStatus)
            ? (rowId, row: IAgent) => activeOrInactiveAgent(rowId, row)
            : undefined,
        }}
        rowActionsExtra={rowActionsExtra}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleNewRecord"],
          },
          handleNewRecord: isShowAction(currentUser, agentPermission.create)
            ? {
                text: translate("create"),
                action: () => history.push(PRIVATE_ROUTES.NETWORK_AGENT_MNT, { tab: "agents" }),
                tooltip: translate("create"),
              }
            : undefined,
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            categories: [
              {
                name: "Status",
                options: [
                  AgentStatus.ACTIVE,
                  AgentStatus.DISABLED,
                  AgentStatus.INVITATION_EXPIRED,
                  AgentStatus.INVITATION_SENT,
                  AgentStatus.REJECTED,
                  AgentStatus.REVOKED,
                ],
              },
            ],
            onChange: handleFilter,
          },
        }}
        showSelection={false}
        positionActionsColumn={"right"}
        showHeaderGrid={true}
        showFooterGrid={true}
        gridCellPadding="16px"
        gridHeaderCellPadding="7px 16px"
      />

      <AppConfirmationModal ref={modalRef} maxWidth="392px" />
    </div>
  );
};
